import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import ATsearch from '@at.govt.nz/search';
import GeocoderService from '../../services/GeocoderService';
import {
  getAddressLabel,
  searchAddresses,
  searchAddressesInStorage,
  getAddressOfCurrentLocation,
  isCurrentLocation,
  isFindOnMap,
  saveRecentSearch,
  combineSearchResults,
  createSelectionsFromAddresses,
  getSearchIconName,
} from '../../services/AddressService';
import { SEARCH_CATEGORY_SVG_ICON } from '../../constants/addressCategories';

import Confirm from '../Confirm';

const DEBOUNCE_TIME = 500; // ms
function renderAddressIcon(address) {
  const iconName = getSearchIconName(address);
  const SvgIcon = SEARCH_CATEGORY_SVG_ICON[iconName];
  if (SvgIcon) {
    return (
      <SvgIcon className="suggestion-item__icon" role="img" />
    );
  }
  return '';
}

function getSectionSuggestions(section) {
  return section?.results ?? [];
}
function renderSectionTitle(section) {
  return section?.title ? <span>{ section.title }</span> : null;
}

class AddressAutosuggest extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selection: null,
      suggestions: [],
      showLocationPermissionRationale: false,
    };

    this.onClearSuggestions = this.onClearSuggestions.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onAddressSuggestionsFetchRequested = this.onAddressSuggestionsFetchRequested.bind(this);
    this.onAddressSuggestionSelected = this.onAddressSuggestionSelected.bind(this);
    this.onClearButtonClick = this.onClearButtonClick.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);

    this.debouncedCombineApiResult = _.debounce(this.combineApiResult, DEBOUNCE_TIME);
  }

  onInputChange(event, { newValue }) {
    const { onTextChange } = this.props;
    onTextChange(newValue);
  }

  onClearSuggestions() {
    this.setState(() => ({ suggestions: [] }));
  }

  onAddressSuggestionsFetchRequested({ value }) {
    const { selection } = this.state;

    if (_.isEmpty(selection) || !_.isEqual(value, getAddressLabel(selection))) {
      this.setState(() => ({ query: value, fetching: !_.isEmpty(value), suggestions: [] }));
      this.querySuggestionsRequest(value);
    }
  }

  onAddressSuggestionSelected(event, { suggestion }) {
    const { onAddressSelected } = this.props;
    onAddressSelected();
    if (isCurrentLocation(suggestion)) {
      this.setState(() => ({ fetching: true }));
      getAddressOfCurrentLocation()
        .then((address) => {
          this.setState(() => ({
            selection: address, suggestions: [], fetching: false, query: address.address,
          }));
          this.handleAddressSelected(address, true);
        })
        .catch((error) => {
          this.handleAddressSelected({ error });
          this.setState(() => ({
            error, selection: null, query: '', fetching: false, showLocationPermissionRationale: true,
          }));
        });
    } else {
      this.setState(() => ({ selection: suggestion }));
      this.handleAddressSelected(suggestion, !isFindOnMap(suggestion));
    }
  }

  onClearButtonClick() {
    const { onAddressCleared, onTextChange } = this.props;
    onTextChange('');
    onAddressCleared();
  }

  composeSuggestionsLiveRegionMessage = (suggestions) => {
    if (!suggestions || suggestions.length < 1) { return ''; }
    const suggestionsCount = suggestions
      .map(s => s.results?.length ?? 0)
      .reduce((p, n) => p + n);

    if (suggestionsCount > 0) { return `${suggestionsCount} suggestions displayed. To navigate use up and down arrow keys.`; }

    return '';
  }

  handleAddressSelected(address, saveSearch) {
    const { onAddressDetermined, storageKey } = this.props;
    onAddressDetermined(address);
    if (saveSearch) {
      saveRecentSearch(storageKey, address);
    }
  }

  combineApiResult(query, localResults) {
    searchAddresses(query).then((apiResults) => {
      const mergedResults = combineSearchResults(localResults, apiResults);
      this.setState(() => ({ suggestions: [{ results: mergedResults }], fetching: false }));
    }).catch((error) => {
      this.setState(() => ({ error, fetching: false }));
    });
  }

  querySuggestionsRequest(query) {
    const { hasCurrentLocation, hasFindOnMap, storageKey } = this.props;
    const trimmedQuery = query?.trim();
    // 1.display a fast result while loading from suggest from geocoder service.
    searchAddressesInStorage(storageKey, trimmedQuery).then((localResults) => {
      this.setState(() => ({
        suggestions: createSelectionsFromAddresses(localResults, trimmedQuery, { hasCurrentLocation, hasFindOnMap }),
      }));
      if (trimmedQuery.length >= GeocoderService.GEOCODER_MIN_QUERY_LENGTH) {
        this.debouncedCombineApiResult(trimmedQuery, localResults);
      } else {
        this.debouncedCombineApiResult.cancel();
        this.setState(() => ({ fetching: false }));
      }
    }).catch(() => {
      this.setState(() => ({ fetching: false }));
    });
  }

  renderSuggestion(address, { isHighlighted }) {
    const addressLabel = getAddressLabel(address);

    let classNames = ["suggestion-item"]
    if (addressLabel === this.props.value && isHighlighted) {
      classNames.push("suggestion-item--highlighted");
    }

    return (
      <div className="suggestion-wrapper">
        <div className={classNames.join(' ')}>
          { renderAddressIcon(address) }
          <span>
            { address.name && <div className="suggestion-item__title">{address.name}</div> }
            <span className="suggestion-item__content">{address.address}</span>
          </span>
        </div>
      </div>
    );
  }

  render() {
    const { suggestions, fetching, showLocationPermissionRationale } = this.state;
    const {
      placeholder, value, hasFrontSpace, id, ariaLabel,
    } = this.props;

    return (
      <div className="address-input-container">
        <Confirm isOpen={showLocationPermissionRationale} showCloseButton={false} showCancelButton={false} confirmLabel="OK" title="Location services not available" onConfirm={() => { this.setState({ showLocationPermissionRationale: false }); }}>
          <p className="location-permission-rationale-content">
            Unfortunately we are unable to find your location due to location services being disabled. Please enable location services to use this feature.
          </p>
        </Confirm>
        <ATsearch
          id={id}
          leftSpace={hasFrontSpace}
          multiSection
          name={id}
          value={value}
          onChange={this.onInputChange}
          onCleared={this.onClearButtonClick}
          fetching={fetching}
          renderSearchButton
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          suggestions={suggestions}
          suggestionsLiveRegionMessage={this.composeSuggestionsLiveRegionMessage(suggestions)}
          onSuggestionsFetchRequested={this.onAddressSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onClearSuggestions}
          onSuggestionSelected={this.onAddressSuggestionSelected}
          getSuggestionValue={getAddressLabel}
          renderSuggestion={this.renderSuggestion}
          getSectionSuggestions={getSectionSuggestions}
          renderSectionTitle={renderSectionTitle}
        />
      </div>
    );
  }
}

AddressAutosuggest.defaultProps = {
  onAddressCleared: () => { /* default empty function */ },
  onAddressSelected: () => { /* default empty function */ },
  onTextChange: () => { /* default empty function */ },
  placeholder: '',
  value: '',
  hasCurrentLocation: false,
  hasFindOnMap: false,
  hasFrontSpace: false,
  ariaLabel: '',
};
AddressAutosuggest.propTypes = {
  id: PropTypes.string.isRequired,
  storageKey: PropTypes.string.isRequired,
  onAddressDetermined: PropTypes.func.isRequired,
  onAddressSelected: PropTypes.func,
  onAddressCleared: PropTypes.func,
  onTextChange: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  hasCurrentLocation: PropTypes.bool,
  hasFindOnMap: PropTypes.bool,
  hasFrontSpace: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default AddressAutosuggest;
